import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { defaultBreadcrumbItems } from 'data/commonData';
import { useRmaDetails } from 'hooks/refunds/useRmaDetails';
import RmaProductsTable from 'components/refunds/RmaProductsTable';
import RmaResolutionBadge from 'components/refunds/RmaResolutionBadge';
import RefundStatusBadge from 'components/refunds/RefundStatusBadge';
import { currencyFormat } from 'helpers/utils';

const RmaDetails: React.FC = () => {
  const { t } = useTranslation();
  const { rmaId } = useParams<{ rmaId: string }>();
  const { 
    rmaDetails, 
    loading, 
    error, 
    getResolutionTypeLabel 
  } = useRmaDetails();
  
  // Breadcrumb items with RMA number if available
  const breadcrumbItems = rmaDetails 
    ? [...defaultBreadcrumbItems, { label: `RMA #${rmaDetails.number || rmaDetails.expedition_number}` }]
    : defaultBreadcrumbItems;
    
  // Early returns for loading, error, and not found states
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" variant="warning">
          <span className="visually-hidden">{t('loading')}</span>
        </Spinner>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <h5 className="mb-3">{t('rmaDetails.messages.unableToLoad')}</h5>
          <Button variant="primary" as={Link} to="/refunds">
            {t('rmaDetails.actions.returnToList')}
          </Button>
        </div>
      </div>
    );
  }

  if (!rmaDetails) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <h5 className="mb-3">{t('rmaDetails.messages.notFound')}</h5>
          <Button variant="primary" as={Link} to="/refunds">
            {t('rmaDetails.actions.returnToList')}
          </Button>
        </div>
      </div>
    );
  }

  // Status color mapping based on status code
  const getStatusColorVariant = (statusCode: string | number) => {
    // Convert string status to number if possible
    const numericStatus = typeof statusCode === 'string' 
      ? parseInt(statusCode, 10) || 0 
      : typeof statusCode === 'number' ? statusCode : 0;
      
    // Map status codes to bootstrap color variants
    const statusMap: Record<number, { 
      border: string, 
      bg: string, 
      text: string, 
      icon: string
    }> = {
      0: { // Pending
        border: 'border-warning',
        bg: 'bg-warning-subtle',
        text: 'text-warning',
        icon: 'clock'
      },
      1: { // Processing
        border: 'border-info',
        bg: 'bg-info-subtle',
        text: 'text-info',
        icon: 'refresh-cw'
      },
      2: { // Resolved
        border: 'border-success',
        bg: 'bg-success-subtle',
        text: 'text-success',
        icon: 'check-circle'
      },
      3: { // Declined
        border: 'border-danger',
        bg: 'bg-danger-subtle',
        text: 'text-danger',
        icon: 'x-circle'
      },
      4: { // Canceled
        border: 'border-secondary',
        bg: 'bg-secondary-subtle',
        text: 'text-secondary',
        icon: 'slash'
      }
    };
    
    return statusMap[numericStatus] || statusMap[0]; // Default to pending if status not found
  };

  // Get status code from the query
  const statusCode = rmaDetails.status?.code;
  
  // Get color variant for the current status
  const colorVariant = getStatusColorVariant(statusCode);

  return (
    <div>
      <div className="mb-9">
        {/* Page title with RMA ID from URL */}
        <h2 className="mb-1">
          {t('rmaDetails.fields.rma')} <span>#{rmaId}</span>
        </h2>
        
        {/* Information bar with expedition number, date and badges */}
        <div className="d-flex flex-wrap flex-between-center mb-5 gap-3">
          <div className="d-flex flex-column gap-2">


            
            {/* Created date with calendar icon */}
            {(rmaDetails.created_at || rmaDetails.creation_date) && (
              <div className="d-flex align-items-center">
                <div className="bg-light bg-opacity-75 rounded-pill py-1 px-3 d-flex align-items-center shadow-sm border border-light-subtle">
                  <FeatherIcon 
                    icon="calendar" 
                    size={14} 
                    className="text-body-secondary me-2" 
                  />
                  <p className="text-body-tertiary mb-0 fs-10 fs-sm-9">
                    {t('rmaDetails.fields.createdDate')}: 
                    <span className="fw-semibold ms-2 text-primary-emphasis">
                      {new Date(rmaDetails.created_at || rmaDetails.creation_date || '').toLocaleDateString(
                        i18next.language === 'fr' ? 'fr-FR' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </p>
                </div>
              </div>
            )}
            
            {/* Customer email if available */}
            {rmaDetails.customer_email && (
              <div className="d-flex align-items-center">
                <div className="bg-light bg-opacity-75 rounded-pill py-1 px-3 d-flex align-items-center shadow-sm border border-light-subtle">
                  <FeatherIcon 
                    icon="mail" 
                    size={14} 
                    className="text-body-secondary me-2" 
                  />
                  <p className="text-body-tertiary mb-0 fs-10 fs-sm-9">
                    {t('rmaDetails.fields.customerEmail')}: 
                    <span className="fw-semibold ms-2 text-primary-emphasis">
                      {rmaDetails.customer_email}
                    </span>
                  </p>
                </div>
              </div>
            )}
            
            {/* Customer info if available */}
            {rmaDetails.customer_name && (
              <div className="d-flex align-items-center">
                <div className="bg-light bg-opacity-75 rounded-pill py-1 px-3 d-flex align-items-center shadow-sm border border-light-subtle">
                  <FeatherIcon 
                    icon="user" 
                    size={14} 
                    className="text-body-secondary me-2" 
                  />
                  <p className="text-body-tertiary mb-0 fs-10 fs-sm-9">
                    {t('rmaDetails.fields.customer')}: 
                    <span className="fw-semibold ms-2 text-primary-emphasis">
                      {rmaDetails.customer_name}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Card */}
        <div className="mb-4">
          <Card className={`border-top border-0 border-2 ${colorVariant.border} shadow-sm rounded-3`}>
            <Card.Body className="d-flex align-items-center p-3">
              <div className={`p-3 ${colorVariant.bg} rounded-3 me-4`}>
                <FeatherIcon 
                  icon={colorVariant.icon} 
                  size={20} 
                  className={colorVariant.text} 
                />
              </div>
              <div>
                <h5 className="mb-2 d-flex align-items-center">
                  {t('rmaDetails.fields.status')}: 
                  <span className="ms-2 d-flex align-items-center">
                    <RefundStatusBadge status={statusCode} className="fs-9" />
                  </span>
                </h5>
                <p className="mb-0 text-body-secondary d-flex align-items-center">
                  {t('rmaDetails.fields.resolutionType')}: 
                  <span className="ms-2">
                    <RmaResolutionBadge resolutionType={rmaDetails.resolution_type} />
                  </span>
                </p>
              </div>
            </Card.Body>
          </Card>
        </div>
        
        {/* Main content */}
        <Row className="g-5 gy-7">
          {/* Left column: products and details */}
          <Col xs={12} xl={8} xxl={9}>
            <div className="mb-6">
              <h5 className="mb-3 fs-6">{t('rmaDetails.fields.products')}</h5>
              <RmaProductsTable products={rmaDetails.products} />
            </div>
            
            {/* RMA & Order Information */}
            <Card className="shadow-sm rounded-3 border-light mb-5">
              <Card.Header className="bg-light d-flex align-items-center py-2 px-3">
                <FeatherIcon icon="info" size={16} className="me-2 text-body-tertiary" />
                <h5 className="mb-0 ">{t('rmaDetails.fields.information')}</h5>
              </Card.Header>
              <Card.Body className="py-2 px-3">
                <div className="p-2">
                  <h6 className="mb-2 d-flex align-items-center">
                    <span className="text-body-tertiary me-2 fs-9 fw-medium" style={{ width: '110px' }}>{t('rmaDetails.fields.id')}:</span>
                    <span className="fw-semibold text-primary">#{rmaId}</span>
                  </h6>
                  {/* Only show expedition number if not null */}
                  {rmaDetails.expedition_number && (
                    <h6 className="mb-2 d-flex align-items-center">
                      <span className="text-body-tertiary me-2 fs-9 fw-medium" style={{ width: '110px' }}>{t('rmaDetails.fields.expeditionNumber')}:</span>
                      <span className="fw-semibold text-primary">{rmaDetails.expedition_number}</span>
                    </h6>
                  )}
                  <h6 className="mb-0 d-flex align-items-center">
                    <span className="text-body-tertiary me-2 fs-9 fw-medium" style={{ width: '110px' }}>{t('rmaDetails.fields.orderNumber')}:</span>
                    <Link to={`/orders/${rmaDetails.order_id}`} className="fw-semibold text-primary-emphasis">
                      #{rmaDetails.order_number}
                    </Link>
                  </h6>
                </div>
              </Card.Body>
            </Card>
            
            {/* Additional Information */}
            {rmaDetails.additional_info && (
              <div className="mt-5">
                <Card className="shadow-sm rounded-3 border-light">
                  <Card.Header className="bg-light d-flex align-items-center py-2 px-3">
                    <FeatherIcon icon="info" size={16} className="me-2 text-body-tertiary" />
                    <h5 className="mb-0 ">{t('rmaDetails.fields.additionalInfo')}</h5>
                  </Card.Header>
                  <Card.Body className="py-2 px-3">
                    <div className="bg-light bg-opacity-50 p-3 rounded-3 border border-light-subtle">
                      <h6 className="mb-0 fst-italic text-primary-emphasis">{rmaDetails.additional_info}</h6>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Col>
          
          {/* Right column: summary */}
          <Col xs={12} xl={4} xxl={3}>
            <Card className="shadow-sm rounded-3 border-light" style={{ position: "sticky", top: "20px" }}>
              <Card.Body className="p-3">
                <Card.Title as="h6" className="mb-3 fs-6">{t('rmaDetails.fields.summary')}</Card.Title>
                
                <div className="mb-3 pb-2 border-bottom">
                  <p className="d-flex justify-content-between align-items-center">
                    <span className="text-body-secondary">{t('rmaDetails.fields.resolutionType')}:</span>
                    <RmaResolutionBadge resolutionType={rmaDetails.resolution_type} />
                  </p>
                  <p className="d-flex justify-content-between align-items-center">
                    <span className="text-body-secondary">{t('rmaDetails.fields.status')}:</span>
                    <RefundStatusBadge status={statusCode} />
                  </p>
                </div>
                
                {/* Display refund amount if resolution type is REFUND */}
                {rmaDetails.resolution_type === 1 && rmaDetails.refunded_amount > 0 && (
                  <div className="mb-0">
                    <h6 className="mb-2 text-body-secondary">{t('rmaDetails.fields.refundInformation')}</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">{t('rmaDetails.fields.refundAmount')}:</span>
                      <span className="fw-bold">
                        {currencyFormat(rmaDetails.refunded_amount, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default RmaDetails;
